// BABA AI - APP.JS - COMPLETE VERSION
// Mobile hamburger menu + Together AI (405B) integration

import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { sendMessage as sendAIMessage } from './services/aiService';

function App() {
  const [chats, setChats] = useState({});
  const [currentChatId, setCurrentChatId] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Load chats from localStorage
  useEffect(() => {
    const savedChats = localStorage.getItem('babaai_chats');
    if (savedChats) {
      const parsedChats = JSON.parse(savedChats);
      setChats(parsedChats);
      const chatIds = Object.keys(parsedChats);
      if (chatIds.length > 0) {
        setCurrentChatId(chatIds[0]);
      }
    } else {
      createNewChat();
    }
  }, []);

  // Save chats to localStorage
  useEffect(() => {
    if (Object.keys(chats).length > 0) {
      localStorage.setItem('babaai_chats', JSON.stringify(chats));
    }
  }, [chats]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chats, currentChatId]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [inputValue]);

  // Create new chat
  const createNewChat = () => {
    const newChatId = Date.now().toString();
    const newChat = {
      id: newChatId,
      title: 'New Chat',
      messages: [],
      createdAt: new Date().toISOString()
    };
    setChats(prev => ({ ...prev, [newChatId]: newChat }));
    setCurrentChatId(newChatId);
    setSidebarOpen(false);
    setError(null);
  };

  // Send message
  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading || !currentChatId) return;

    const userMessage = {
      role: 'user',
      content: inputValue,
      timestamp: new Date().toISOString()
    };

    // Add user message
    setChats(prev => ({
      ...prev,
      [currentChatId]: {
        ...prev[currentChatId],
        messages: [...prev[currentChatId].messages, userMessage]
      }
    }));

    // Auto-rename chat based on first message
    if (chats[currentChatId].messages.length === 0) {
      const title = inputValue.slice(0, 30) + (inputValue.length > 30 ? '...' : '');
      setChats(prev => ({
        ...prev,
        [currentChatId]: {
          ...prev[currentChatId],
          title
        }
      }));
    }

    setInputValue('');
    setIsLoading(true);
    setError(null);

    try {
      // Prepare messages for API (convert to Together AI format)
      const apiMessages = chats[currentChatId].messages.concat(userMessage).map(msg => ({
        role: msg.role === 'user' ? 'user' : 'assistant',
        content: msg.content
      }));

      // Call Together AI
      const aiResponse = await sendAIMessage(apiMessages);

      const aiMessage = {
        role: 'assistant',
        content: aiResponse,
        timestamp: new Date().toISOString()
      };

      setChats(prev => ({
        ...prev,
        [currentChatId]: {
          ...prev[currentChatId],
          messages: [...prev[currentChatId].messages, aiMessage]
        }
      }));

    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.message || 'Failed to get AI response');
      
      // Add error message to chat
      const errorMessage = {
        role: 'assistant',
        content: `Error: ${err.message || 'Failed to get response. Please check your API key and try again.'}`,
        timestamp: new Date().toISOString(),
        isError: true
      };

      setChats(prev => ({
        ...prev,
        [currentChatId]: {
          ...prev[currentChatId],
          messages: [...prev[currentChatId].messages, errorMessage]
        }
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Delete current chat
  const deleteCurrentChat = () => {
    if (!currentChatId || Object.keys(chats).length === 1) {
      createNewChat();
      return;
    }

    const chatIds = Object.keys(chats);
    const currentIndex = chatIds.indexOf(currentChatId);
    const nextChatId = chatIds[currentIndex + 1] || chatIds[currentIndex - 1];

    const newChats = { ...chats };
    delete newChats[currentChatId];
    setChats(newChats);
    setCurrentChatId(nextChatId);
  };

  // Filter chats
  const filteredChats = Object.values(chats).filter(chat =>
    chat.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Toggle sidebar on mobile
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Close sidebar when clicking overlay
  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Switch chat and close sidebar on mobile
  const switchChat = (chatId) => {
    setCurrentChatId(chatId);
    setSidebarOpen(false);
    setError(null);
  };

  const currentChat = chats[currentChatId];

  return (
    <div className="app">
      {/* Hamburger Menu (Mobile only) */}
      <div className="hamburger-menu" onClick={toggleSidebar}>
        <div className="hamburger-icon">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>

      {/* Sidebar Overlay (Mobile only) */}
      <div 
        className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
        onClick={closeSidebar}
      ></div>

      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'active' : ''}`}>
        <div className="sidebar-header">
          <h1>🖤 Baba AI</h1>
          <input
            type="text"
            className="search-box"
            placeholder="Search chats..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="chat-list">
          {filteredChats.map(chat => (
            <div
              key={chat.id}
              className={`chat-item ${chat.id === currentChatId ? 'active' : ''}`}
              onClick={() => switchChat(chat.id)}
            >
              <div className="chat-item-title">{chat.title}</div>
              <div className="chat-item-preview">
                {chat.messages.length === 0
                  ? 'No messages yet'
                  : chat.messages[chat.messages.length - 1].content.slice(0, 50)}
              </div>
            </div>
          ))}
        </div>

        <div className="sidebar-footer">
          <button className="btn" onClick={createNewChat}>
            + New Chat
          </button>
          <button className="btn btn-secondary" onClick={deleteCurrentChat}>
            🗑️
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        <div className="chat-container">
          {error && (
            <div className="error-banner">
              ⚠️ {error}
            </div>
          )}

          <div className="messages">
            {currentChat?.messages.map((msg, idx) => (
              <div key={idx} className={`message ${msg.role} ${msg.isError ? 'error' : ''}`}>
                <div>{msg.content}</div>
                <div className="timestamp">
                  {new Date(msg.timestamp).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="message assistant">
                <div className="loading">●●●</div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="input-area">
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message... (Shift+Enter for new line)"
              rows={1}
              disabled={isLoading}
            />
            <button onClick={sendMessage} disabled={isLoading || !inputValue.trim()}>
              {isLoading ? '...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
